import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BBCode, hasBBCode } from "./bbcode";
import { ROLE_STYLES, timeAgo } from "@/lib/api";

export const avatarSrc = (user) => (user?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL || ""}${user.avatar_url}` : null);

export const Avatar = ({ user, size = "md", className = "", src }) => {
  const s = { sm: "h-7 w-7 text-[10px]", md: "h-10 w-10 text-sm", lg: "h-16 w-16 text-xl", xl: "h-24 w-24 text-3xl" }[size];
  if (!user) return <div className={`${s} rounded-full bg-white/10 ${className}`} />;
  const img = src || avatarSrc(user);
  if (img) {
    return <img src={img} alt={user.username} className={`${s} shrink-0 rounded-full object-cover ring-1 ring-white/10 ${className}`} data-testid={`avatar-${user.username}`} />;
  }
  return (
    <div
      className={`${s} grid shrink-0 place-items-center rounded-full font-display font-bold uppercase text-black ${className}`}
      style={{ background: user.avatar_color || "#5eb1ff" }}
      data-testid={`avatar-${user.username}`}
    >
      {user.username?.slice(0, 2)}
    </div>
  );
};

export const RoleBadge = ({ role, label, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ${ROLE_STYLES[role] || ROLE_STYLES.player} ${className}`}
    data-testid={`role-badge-${role}`}
  >
    {label}
  </span>
);

export const UserChip = ({ user, size = "sm", showRole = false }) => {
  if (!user) return <span className="text-white/40">ismeretlen</span>;
  return (
    <Link to={`/forum/u/${user.username}`} className="inline-flex items-center gap-2 text-white/80 transition-colors hover:text-white" data-testid={`user-chip-${user.username}`}>
      <Avatar user={user} size={size} />
      <span className="font-medium">{user.username}</span>
      {showRole && <RoleBadge role={user.role} label={user.role_label} />}
    </Link>
  );
};

export const Time = ({ iso, className = "" }) => (
  <time dateTime={iso} title={iso ? new Date(iso).toLocaleString("hu-HU") : ""} className={`font-mono text-[11px] text-white/40 ${className}`}>
    {timeAgo(iso)}
  </time>
);

export const Markdown = ({ children }) => (
  <div className="prose-forum text-[15px] leading-relaxed text-white/80">
    {hasBBCode(children) ? <BBCode>{children}</BBCode> : <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>}
  </div>
);

export const Empty = ({ text, testId }) => (
  <div className="rounded-3xl border border-dashed border-white/10 p-12 text-center font-mono text-xs tracking-wider text-white/40" data-testid={testId}>
    {text}
  </div>
);

export const Spinner = () => (
  <div className="flex justify-center py-20" data-testid="loading-spinner">
    <span className="h-6 w-6 animate-spin rounded-full border-2 border-white/15 border-t-[var(--glow)]" />
  </div>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`rounded-3xl border border-white/[0.08] bg-[#111113] ${className}`} {...rest}>{children}</div>
);

export const Btn = ({ children, variant = "primary", className = "", ...rest }) => {
  const v = {
    primary: "bg-white text-black hover:shadow-[0_0_0_1px_rgba(94,177,255,0.5),0_10px_30px_-8px_rgba(94,177,255,0.6)]",
    ghost: "border border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.1]",
    danger: "border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20",
  }[variant];
  return (
    <button className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 ${v} ${className}`} {...rest}>
      {children}
    </button>
  );
};

export const Input = ({ className = "", ...rest }) => (
  <input
    className={`w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-[rgba(94,177,255,0.6)] ${className}`}
    {...rest}
  />
);

export const Textarea = ({ className = "", ...rest }) => (
  <textarea
    className={`w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-[rgba(94,177,255,0.6)] ${className}`}
    {...rest}
  />
);
